/**
 * La scheda «Utile vero» (Davide 02/11/2026): quanto resta davvero dopo
 * pubblicita', affitto, software e consulenti, presi dalle fatture d'acquisto;
 * e il sito Kery contro i marketplace, con la pubblicita' di ognuno.
 */

import type { BiCostiStruttura, BiGruppoCanale } from "../biApi";
import { eur, intero, n, perc } from "../biTesti";
import { InfoTip } from "./InfoTip";
import { Riquadro } from "./Riquadro";

const CATEGORIE: Record<string, { nome: string; spiega: string }> = {
  merce: { nome: "Merce", spiega: "Le fatture dei fornitori della merce." },
  spedizioni: { nome: "Spedizioni", spiega: "Bracchi, BRT e Poste." },
  commissioni: {
    nome: "Commissioni dei marketplace",
    spiega: "Maisons du Monde, Amazon, Metro, Leroy Merlin, Vente-Unique, Brico Bravo, Stripe.",
  },
  pubblicita: { nome: "Pubblicità", spiega: "Google e Meta per il sito, Amazon Ads per Amazon." },
  sede: { nome: "Sede e servizi", spiega: "Sub affitto e servizi amministrativi (MATECPRO)." },
  software: { nome: "Software e internet", spiega: "Programmi, cloud, siti, telefono." },
  consulenze: { nome: "Consulenti", spiega: "Commercialista, avvocati, formazione." },
  ambiente: { nome: "Contributi ambientali", spiega: "EPR Francia (Citeo, Ecomaison)." },
  abbonamenti: {
    nome: "Abbonamenti marketplace",
    spiega: "ManoMano: 110 € al mese più le commissioni del mese, nella stessa fattura.",
  },
  acquisti: { nome: "Acquisti d'ufficio", spiega: "Piccoli acquisti per l'azienda." },
  trasferte: { nome: "Pranzi e trasferte", spiega: "Ristoranti e viaggi di lavoro." },
  banca: {
    nome: "Spese di banca",
    spiega:
      "Commissioni, canoni, bollo dall'estratto conto caricato in Saldi. Non i bonifici ai fornitori: quelli sono già sugli ordini.",
  },
  da_classificare: {
    nome: "Da classificare",
    spiega:
      "Fornitori che OM non conosce ancora. Si tolgono lo stesso: in dubbio, il numero più prudente.",
  },
};

function nomeCategoria(c: string): string {
  return CATEGORIE[c]?.nome ?? c;
}

const GRUPPI: Record<string, string> = {
  sito: "Sito (Kery e Bob Gardens)",
  amazon: "Amazon",
  marketplace: "Altri marketplace",
};

// ── L'utile vero ─────────────────────────────────────────────────────────────

export function UtileVeroTestata({ c }: { c: BiCostiStruttura }) {
  const u = c.utile;
  const voci = [
    { nome: "Margine degli ordini", valore: eur(u.margin), segno: "" },
    { nome: "Resi e sostituzioni", valore: eur(u.post_vendita), segno: "−" },
    ...(n(u.bonifici) > 0
      ? [{ nome: "Bonifici ai fornitori", valore: eur(u.bonifici), segno: "−" }]
      : []),
    { nome: "Costi di struttura", valore: eur(u.struttura), segno: "−" },
  ];
  const quota = n(u.margin) > 0 ? (n(u.utile) / n(u.margin)) * 100 : null;
  return (
    <section
      aria-label="Utile vero"
      className="bi-entra relative mb-4 overflow-hidden rounded-[22px] bg-rail px-7 py-6 text-white max-md:rounded-[18px] max-md:px-4 max-md:py-5"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-28 -top-36 h-[320px] w-[520px] rounded-full bg-[radial-gradient(closest-side,rgba(201,162,77,0.30),transparent)] blur-2xl"
      />
      <div className="relative grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-10 max-lg:grid-cols-1 max-lg:gap-6">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-[14px] font-bold">Utile vero del periodo</h2>
            <InfoTip
              spiegazione={{
                titolo: "Utile vero",
                testo:
                  "Il margine degli ordini meno resi e sostituzioni e meno le spese che non stanno in nessun ordine: pubblicità, sede, software, consulenti, banca. Le spese vengono dalle fatture d'acquisto con data nel periodo. Senza IVA, prima delle tasse.",
                conto: [
                  { etichetta: "Margine degli ordini", valore: eur(u.margin, 2) },
                  { segno: "−", etichetta: "Resi e sostituzioni", valore: eur(u.post_vendita, 2) },
                  ...(n(u.bonifici) > 0
                    ? [
                        {
                          segno: "−" as const,
                          etichetta: "Bonifici ai fornitori",
                          valore: eur(u.bonifici, 2),
                        },
                      ]
                    : []),
                  { segno: "−", etichetta: "Costi di struttura", valore: eur(u.struttura, 2) },
                  { segno: "=", etichetta: "Utile vero", valore: eur(u.utile, 2) },
                ],
                incerto:
                  "Le fatture arrivano a volte il mese dopo la spesa: su un periodo corto l'utile balla. Meglio guardarlo su un trimestre o un anno.",
              }}
            />
          </div>
          <p
            className={`mt-2 font-display text-[clamp(2.4rem,4.2vw,4rem)] font-bold leading-[0.95] tracking-[-0.035em] tabular-nums ${n(u.utile) < 0 ? "text-[#f0a58a]" : ""}`}
          >
            {eur(u.utile)}
          </p>
          <p className="mt-3 text-[14px] font-semibold">
            {quota == null ? (
              "Nessun margine nel periodo."
            ) : (
              <>
                Del margine ti resta il <b className="font-display text-[20px]">{perc(quota)}</b>.
              </>
            )}
          </p>
        </div>
        <ul className="grid grid-cols-3 gap-3 self-end max-sm:grid-cols-1">
          {voci.map((v) => (
            <li key={v.nome} className="min-w-0 rounded-[14px] bg-white/[0.07] px-4 py-3.5">
              <span className="block text-[12.5px] font-bold">{v.nome}</span>
              <span className="mt-1.5 block font-display text-[24px] font-bold leading-none tabular-nums">
                {v.segno && <span className="mr-1">{v.segno}</span>}
                {v.valore}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// ── Dove vanno i soldi ───────────────────────────────────────────────────────

export function SpeseFatture({ c }: { c: BiCostiStruttura }) {
  const struttura = c.categorie.filter((x) => !x.nel_margine);
  const nelMargine = c.categorie.filter((x) => x.nel_margine);
  const massimo = Math.max(1, ...struttura.map((x) => n(x.totale)));
  return (
    <Riquadro
      titolo="Le spese fuori dagli ordini"
      spiegazione={{
        titolo: "Le spese fuori dagli ordini",
        testo:
          "Tutte le fatture d'acquisto del periodo, per tipo di spesa. Quelle di sopra si tolgono dal margine. Merce, spedizioni e commissioni sono già dentro il margine di ogni ordine: si vedono sotto, per confronto, ma non si tolgono di nuovo.",
      }}
    >
      <ul className="space-y-3">
        {struttura.map((x) => (
          <li key={x.categoria}>
            <details className="group">
              <summary className="flex cursor-pointer list-none items-center gap-3">
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5 text-[13.5px] font-bold">
                    {nomeCategoria(x.categoria)}
                    {x.categoria === "da_classificare" && (
                      <span className="rounded-full bg-amber-soft px-1.5 text-[11px] font-bold">
                        da guardare
                      </span>
                    )}
                  </span>
                  <span className="mt-1 block h-[7px] overflow-hidden rounded-full bg-paper-2">
                    <span
                      className="block h-full rounded-full bg-[#c9a24d]"
                      style={{ width: `${(n(x.totale) / massimo) * 100}%` }}
                    />
                  </span>
                </span>
                <span className="font-display text-[17px] font-bold tabular-nums">
                  {eur(x.totale)}
                </span>
              </summary>
              <p className="mt-2 text-[12px] font-semibold">{CATEGORIE[x.categoria]?.spiega}</p>
              <ul className="mt-1.5 divide-y divide-line text-[12.5px]">
                {x.fornitori.map((f) => (
                  <li key={f.nome} className="flex justify-between gap-3 py-1">
                    <span className="min-w-0 truncate">{f.nome}</span>
                    <span className="tabular-nums">
                      {eur(f.totale)} · {intero(f.documenti)} doc.
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          </li>
        ))}
        {struttura.length === 0 && (
          <li className="text-[13px] font-semibold">Nessuna fattura di spesa nel periodo.</li>
        )}
      </ul>
      {nelMargine.length > 0 && (
        <div className="mt-5 border-t border-line pt-3">
          <p className="text-[12px] font-bold">Già dentro il margine degli ordini</p>
          <ul className="mt-1.5 divide-y divide-line text-[12.5px]">
            {nelMargine.map((x) => (
              <li key={x.categoria} className="flex justify-between gap-3 py-1">
                <span>{nomeCategoria(x.categoria)}</span>
                <span className="tabular-nums">{eur(x.totale)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Riquadro>
  );
}

// ── Sito contro marketplace ──────────────────────────────────────────────────

function Riga({ g }: { g: BiGruppoCanale }) {
  return (
    <tr>
      <td className="py-2.5 pr-3 font-bold">{GRUPPI[g.group] ?? g.group}</td>
      <td className="py-2.5 pr-3 text-right tabular-nums">{intero(g.orders)}</td>
      <td className="py-2.5 pr-3 text-right tabular-nums">{eur(g.net_revenue)}</td>
      <td className="py-2.5 pr-3 text-right tabular-nums">{eur(g.margin)}</td>
      <td className="py-2.5 pr-3 text-right tabular-nums">
        {n(g.ads) > 0 ? `−${eur(g.ads)}` : "—"}
      </td>
      <td
        className={`py-2.5 pr-3 text-right font-bold tabular-nums ${n(g.margin_after_ads) < 0 ? "text-[#c96442]" : ""}`}
      >
        {eur(g.margin_after_ads)}
      </td>
      <td className="py-2.5 pr-3 text-right tabular-nums">
        {g.ads_per_order == null ? "—" : eur(g.ads_per_order, 2)}
      </td>
      <td className="py-2.5 text-right tabular-nums">
        {g.roas == null ? "—" : `${String(g.roas).replace(".", ",")}×`}
      </td>
    </tr>
  );
}

export function SitoMarketplace({ c }: { c: BiCostiStruttura }) {
  return (
    <Riquadro
      titolo="Sito contro marketplace"
      spiegazione={{
        titolo: "Sito contro marketplace",
        testo:
          "Il sito Kery (Shopify, e il vecchio Bob Gardens) contro Amazon e gli altri marketplace. Al margine degli ordini si toglie la pubblicità che porta clienti a quel canale: Google e Meta per il sito, Amazon Ads per Amazon. Gli altri marketplace non fanno pubblicità a pagamento.",
        conto: [
          { etichetta: "Costo pubblicità per ordine", valore: "pubblicità ÷ ordini" },
          { etichetta: "Ritorno (ROAS)", valore: "venduto senza IVA ÷ pubblicità" },
        ],
        incerto:
          "La pubblicità viene dalle fatture con data nel periodo: Google fattura a soglia, a volte il mese dopo.",
      }}
    >
      <div className="overflow-x-auto max-md:-mx-4 max-md:px-4">
        <table className="w-full min-w-[720px] text-[13px]">
          <thead>
            <tr className="border-b border-line text-left text-[12px] font-bold">
              <th className="py-2 pr-3">Canale</th>
              <th className="py-2 pr-3 text-right">Ordini</th>
              <th className="py-2 pr-3 text-right">Venduto</th>
              <th className="py-2 pr-3 text-right">Margine</th>
              <th className="py-2 pr-3 text-right">Pubblicità</th>
              <th className="py-2 pr-3 text-right">Margine dopo</th>
              <th className="py-2 pr-3 text-right">Pubbl. per ordine</th>
              <th className="py-2 text-right">Ritorno</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {c.canali.map((g) => (
              <Riga key={g.group} g={g} />
            ))}
          </tbody>
        </table>
      </div>
    </Riquadro>
  );
}
